if (!customElements.get("pdp-drawer")) {
    customElements.define(
        "pdp-drawer",
        class PdpDrawer extends HTMLElement {
            constructor() {
                super();

                this.drawerClass = "wt-pdp-drawer";
                this.drawer = this;
                this.classDrawerActive = `${this.drawerClass}--open`;
                this.pageOverlayClass = "page-overlay-pdp";
                this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
                this.body = document.body;

                this.triggerQuery = [
                    ".pdp-drawer--trigger",
                    ".wt-pdp-drawer__close",
                    `.${this.pageOverlayClass}`,
                ].join(", ");

                this.isOpen = false;
                this.lastTrigger = null;
            }

            connectedCallback() {
                this.init();
            }

            getFocusableElements() {
                const focusableSelector = "button, [href], input, select, [tabindex]";
                const focusableElements = Array.from(
                    this.querySelectorAll(focusableSelector),
                ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);

                return {
                    first: focusableElements[0],
                    last: focusableElements[focusableElements.length - 1],
                };
            }

            temporaryHideFocusVisible() {
                document.body.classList.add("no-focus-visible");
                setTimeout(() => {
                    document.body.classList.remove("no-focus-visible");
                }, 200);
            }

            onToggle(viaKeyboard = false) {
                if (this.hasAttribute("open")) {
                    this.removeAttribute("open");
                    this.isOpen = false;
                    this.setAttribute("aria-hidden", "true");
                    if (this.lastTrigger) {
                        this.lastTrigger.setAttribute("aria-expanded", "false");
                        if (viaKeyboard) {
                            this.lastTrigger.focus();
                        }
                    }
                } else {
                    this.setAttribute("open", "");
                    this.isOpen = true;
                    this.setAttribute("aria-hidden", "false");
                    if (this.lastTrigger) {
                        this.lastTrigger.setAttribute("aria-expanded", "true");
                    }
                    const closeBtn = this.querySelector(".wt-pdp-drawer__close");
                    if (closeBtn) closeBtn.focus();
                }
                this.temporaryHideFocusVisible();
            }

            toggleDrawerClasses(viaKeyboard = false) {
                this.onToggle(viaKeyboard);
                this.drawer.classList.toggle(this.classDrawerActive);
                this.body.classList.toggle(this.activeOverlayBodyClass);

                const overlay = document.querySelector(`.${this.pageOverlayClass}`);
                if (overlay) {
                    const isOpen = this.drawer.classList.contains(this.classDrawerActive);
                    overlay.setAttribute("aria-hidden", isOpen ? "false" : "true");
                }
            }

            setActiveTab(tabKey) {
                const tabTriggers = this.querySelectorAll(
                    ".wt-pdp-drawer__tab__trigger",
                );
                const tabContents = this.querySelectorAll(
                    ".wt-pdp-drawer__tab__content",
                );

                tabTriggers.forEach((t) =>
                    t.classList.remove("wt-pdp-drawer__tab__trigger--active"),
                );
                tabContents.forEach((c) => c.classList.remove("is-active"));

                const matchingTrigger = this.querySelector(`[data-tab-target="${tabKey}"]`);
                if (matchingTrigger) {
                    matchingTrigger.classList.add(
                        "wt-pdp-drawer__tab__trigger--active",
                    );
                }

                const matchingContent = this.querySelector(
                    `[data-tab-content="${tabKey}"]`,
                );
                if (matchingContent) {
                    matchingContent.classList.add("is-active");
                }
            }

            init() {
                // Trap focus & close on Escape
                this.addEventListener("keydown", (e) => {
                    if (!this.isOpen) return;
                    const {first, last} = this.getFocusableElements();
                    const isTab = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
                    const isEsc =
                        e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";

                    if (isEsc) {
                        e.preventDefault();
                        this.toggleDrawerClasses(true);
                    } else if (isTab) {
                        // Focus trap
                        if (e.shiftKey && document.activeElement === first) {
                            e.preventDefault();
                            last.focus();
                        } else if (!e.shiftKey && document.activeElement === last) {
                            e.preventDefault();
                            first.focus();
                        }
                    }
                });

                document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
                    trigger.addEventListener("click", (e) => {
                        e.preventDefault();

                        const isOpeningTrigger = trigger.classList.contains("pdp-drawer--trigger");
                        const isClosingTrigger = trigger.classList.contains("wt-pdp-drawer__close") || trigger.classList.contains("page-overlay-pdp");
                        const isKeyboardActivation = e.detail === 0;

                        if (isOpeningTrigger) {
                            const isFocusable = trigger.matches('button, a[href], [tabindex]:not([tabindex="-1"])');
                            if (isFocusable) {
                                this.lastTrigger = trigger;
                            } else {
                                const focusableChild = trigger.querySelector('button, a[href], [tabindex]:not([tabindex="-1"])');
                                this.lastTrigger = focusableChild || trigger;
                            }
                        }

                        const closeBtn = this.querySelector(".wt-pdp-drawer__close");

                        if (e.detail > 0) {
                            closeBtn.classList.add("remove-focus");
                        } else {
                            closeBtn.classList.remove("remove-focus");
                        }

                        const newTitle = trigger.querySelector('div.drawer__trigger-tittle')?.innerHTML || '';
                        const newContent = trigger.querySelector('div.drawer__trigger-content')?.innerHTML || '';

                        const drawer = document.querySelector('.wt-pdp-drawer');
                        const titleElem = drawer.querySelector('.wt-drawer__title__text');
                        const contentElem = drawer.querySelector('.wt-pdp-drawer__body');

                        if (titleElem) titleElem.innerHTML = newTitle;
                        if (contentElem) contentElem.innerHTML = newContent;

                        const openTab = trigger.dataset.openDrawer;

                        const shouldRestoreFocus = isClosingTrigger && isKeyboardActivation;
                        this.toggleDrawerClasses(shouldRestoreFocus);

                        if (openTab) {
                            this.setActiveTab(openTab);
                        }
                    });
                });
            }
        }
    );
}
